import { 
    CHART_VISUAL_TYPES, 
    LOGIC_NODE_TYPES, 
    TRIGGER_NODE_TYPES 
} from '../config/nodeVisualConfig';
import { formatNodeValue } from '../../../../../utils/toDisplayText';

//! broker-qty-semantics (iter-7) — SINGLE SOURCE OF TRUTH for "is this instrument a derivative
//! (F&O)?". Shared here (a util BOTH StrategyBuilder.jsx and components/TradeExecutionStep.jsx
//! already import) instead of duplicating the literal in each file. Drives StrategyBuilder's `isFnO`
//! display flag AND the OrderCard's expired/unresolved-lot hint (TradeExecutionStep). Note: the
//! continuous-mode auto-roll decision uses the TIGHTER {FUTURE, OPTION} set (mirroring the backend
//! readiness gate's _ROLLABLE_INSTRUMENT_TYPES, deployment_service.py:297) — NOT this broader set —
//! so display can never roll on a type the gate would not.
export const DERIVATIVE_INSTRUMENT_TYPES = ['FUTURE', 'OPTION', 'FUT', 'OPT'];

/*>>> 50-21 R12 — ONE auto-assembled-label classifier (replaces the old generic-label list + the inline generic check) <<<*/
//* A stored `data.label` is "auto-assembled" when it looks like text a tool generated (a comparison
//* sentence, a gate sentence, a toolbox default, a placeholder id). Anything else is an authored
//* name an agent or user typed and is carried over read-side (no migration, no write-back).
//* Sample strings: custom = "T9 calls up", "EMA up", "NOTE", "KNOT", "not ready", "and then";
//* auto = "EMA9 (9) 1m < EMA21 (21) 1m", "Logic (AND)", "Math (+)", "NOT ready", "Comparison".
const GENERIC_DEFAULT_LABELS = [
    'Comparator', 'Comparison', 'Logic Gate', 'Logical', 'Arithmetic', 'Node', 'Math',
    'Time Window', 'Condition',
];
const AUTO_OPERATOR_GLYPHS = /[<>↑↓≥≤]|==/;
const AUTO_BOOLEAN_WORDS = /\s(?:AND|OR)\s|(?:^|\s)NOT(?:\s|$)/;
export const isAutoAssembledLabel = (label) => {
    if (label == null) return true;
    const text = String(label).trim();
    if (!text) return true;
    if (/^node_\d+/.test(text)) return true;
    if (GENERIC_DEFAULT_LABELS.includes(text)) return true;
    if (/^Logic(?: \(?[A-Z]+\)?)?$/.test(text)) return true;      // "Logic", "Logic AND", "Logic (AND)"
    if (text.startsWith('Logic (')) return true;                    // main parity: any "Logic (...)" is a toolbox default
    if (/^Math(?: \(.*\))?$/.test(text)) return true;             // "Math", "Math (+)"
    if (/^Time window$/i.test(text)) return true;                 // A-8: ONLY the generic default; "Time window open" is authored
    if (AUTO_OPERATOR_GLYPHS.test(text)) return true;
    if (AUTO_BOOLEAN_WORDS.test(text)) return true;
    return false;
};

//--> the record entry's custom name: a string, trimmed; empty / whitespace-only / non-string = unset
export const getCustomName = (recordEntry) => {
    const value = recordEntry?.custom_name;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed || null;
};

/**
 * Resolves a descriptive label for a node based on its type, data, and connections.
 * 
 * @param {Object} node - The target node to resolve a label for.
 * @param {Array} nodes - All nodes in the flow (for lookups).
 * @param {Array} edges - All edges in the flow (for connection tracing).
 * @returns {string} The resolved smart label.
 */
/*>>> 50-17 — comparator input lookback (offset = bars ago) <<<*/
//  The ONE reader of a comparator input's `offset` for display: '' for the current bar, else 'N bar(s) ago'.
export const barsAgoTag = (input) => {
    const n = Math.trunc(Number(input?.offset) || 0);
    if (n < 1) return '';
    return `${n} bar${n === 1 ? '' : 's'} ago`;
};

//--> ONE graph-data-only title for a data source everywhere it is named. `instrument` is already part of the
//    saved node; this deliberately performs no active-contract or instrument-catalogue lookup.
export const dataSourceTitle = (node) => {
    const data = node?.data || {};
    const instrument = data.instrument;
    const instrumentLabel = typeof instrument === 'string'
        ? instrument
        : instrument?.symbol || instrument?.name || instrument?.trading_symbol;
    return String(data.label || instrumentLabel || 'Market Data');
};

//--> ONE linked-operand name rule: a market-data link is the field it reads; every other source keeps its
//    existing smart label. The source title remains separate context rather than replacing the selected field.
export const resolveOperandSourceName = (sourceNode, field, nodes = [], edges = [], depth = 0) => {
    if (sourceNode?.type === 'data_source') {
        return String(field || sourceNode.data?.field || 'close');
    }
    return sourceNode ? resolveSmartLabel(sourceNode, nodes, edges, depth + 1) : null;
};

/**
 * Resolve one comparator operand exactly as it appears in the Flow sentence.
 * Graph edges remain the wiring authority; an unwired constant falls back to
 * the value stored in the comparator input.
 */
export const resolveOperandName = (node, index, nodes = [], edges = [], depth = 0, record = {}) => {
    const input = node?.data?.inputs?.[index];
    const handleIds = [String(index), `input_${index}`, input?.id].filter(Boolean);
    const edge = edges.find((candidate) => (
        candidate.target === node?.id && handleIds.includes(candidate.targetHandle)
    ));
    const sourceNode = nodes.find((candidate) => candidate.id === (edge?.source || input?.nodeId));
    const constantValue = !input?.nodeId && input?.type === 'constant'
        && input.value != null && input.value !== ''
        ? String(input.value)
        : null;
    //  A-4: with a record, an operand that is a NAMED condition shows its authored name (same rule as
    //  resolveDisplayName); without one the Flow-sentence behaviour is unchanged.
    const authored = sourceNode ? resolveAuthoredName(sourceNode, record[sourceNode.id]) : null;
    const name = authored
        || resolveOperandSourceName(sourceNode, input?.field || edge?.sourceHandle, nodes, edges, depth)
        || constantValue
        || getNodeName(sourceNode, nodes, edges);
    return name;
};

//--> the Flow-sentence operand: its name (resolveOperandName, no lookback) plus ' [N bars ago]' when it reads back
export const resolveOperandLabel = (node, index, nodes = [], edges = [], depth = 0, record = {}) => {
    const name = resolveOperandName(node, index, nodes, edges, depth, record);
    const lookback = barsAgoTag(node?.data?.inputs?.[index]);
    return `${name}${lookback ? ` [${lookback}]` : ''}`;
};

/*>>> 50-20 — ONE comparator operator map (Flow sentence symbol + Tree words) <<<*/
//  `symbol` is what resolveSmartLabel writes into the Flow sentence; the Tree sentence shows the words for a
//  crossing (an arrow alone reads badly between two chips) and the symbol otherwise (comparatorOperatorText).
export const COMPARATOR_OPS = {
    gt: { symbol: '>', words: 'above', label: 'Greater Than', glyph: '>' },
    lt: { symbol: '<', words: 'below', label: 'Less Than', glyph: '<' },
    gte: { symbol: '≥', words: 'at or above', label: 'Greater/Equal', glyph: '≥' },
    lte: { symbol: '≤', words: 'at or below', label: 'Less/Equal', glyph: '≤' },
    eq: { symbol: '==', words: 'equals', label: 'Equal', glyph: '=' },
    crosses_above: { symbol: '↑', words: 'crosses above', label: 'Crosses Above', glyph: 'Cross ↑' },
    crosses_below: { symbol: '↓', words: 'crosses below', label: 'Crosses Below', glyph: 'Cross ↓' },
};

export const comparatorOperatorText = (operator) => {
    const op = COMPARATOR_OPS[operator];
    if (!op) return operator || '?';
    return String(operator).startsWith('crosses_') ? op.words : op.symbol;
};

/** The shared classifier for protected model-regime indicator nodes. */
export const isModelNode = (node) => (
    node?.type === 'indicator'
    && String(node?.data?.indicator || '').toLowerCase() === 'model_regime'
);

/** Fresh default input slots for a logic gate. */
export const defaultLogicInputs = (operator = 'and') => {
    const count = operator === 'not' ? 1 : 2;
    return Array.from({ length: count }, (_, index) => ({
        type: 'link',
        offset: 0,
        id: `input_${index}`,
    }));
};

//  F-2: a missing record is an EMPTY record (default `{}` here and in resolveOperandLabel) - `undefined` and `{}` give identical text.
//  F-arb-9: the optional trailing `record` (node id -> record entry) makes a gate's auto title compose its
//  upstream conditions' DISPLAY names (record custom name if set, else their auto text) - the same rule
//  `resolveOperandLabel` applies to the input chips. Without a record the Flow-sentence behaviour is unchanged.
export const resolveSmartLabel = (node, nodes = [], edges = [], depth = 0, record = {}) => {
    if (!node) return 'Unknown Node';
    if (depth > 2) return getNodeName(node, nodes, edges); // Prevent infinite recursion

    try {
        if (node.type === 'constant') {
            const value = node.data?.value;
            const valueLabel = value === undefined || value === null || value === ''
                ? 'Constant'
                : String(value);
            const authoredLabel = String(node.data?.label || '').trim();
            return authoredLabel && authoredLabel !== valueLabel
                ? `${valueLabel} · ${authoredLabel}`
                : valueLabel;
        }

        // 2. Handle Comparators (e.g. RSI (14) > 50)
        // ALWAYS recalculate for consistency, unless we add a specific "customLabel" flag later
        if (node.type === 'comparator') {
            const leftName = resolveOperandLabel(node, 0, nodes, edges, depth, record);
            const rightName = resolveOperandLabel(node, 1, nodes, edges, depth, record);

            const op = COMPARATOR_OPS[node.data?.operator]?.symbol || node.data?.operator || '?';

            return `${leftName} ${op} ${rightName}`;
        }

        // 3. Handle Logic Gates (e.g. A AND B)
        if (node.type === 'logic_gate') {
            const incoming = edges.filter(e => e.target === node.id);
            const names = incoming.map(e => {
                const src = nodes.find(n => n.id === e.source);
                if (!src) return 'Val';

                //  F-arb-9: an upstream condition's authored name (record custom name / legacy custom label)
                const authored = resolveAuthoredName(src, record[src.id]);
                if (authored) return authored;

                // Recursive Logic with depth check
                if (src.type === 'logic_gate') return (src.data?.operator || 'Logic').toUpperCase();
                if (src.type === 'comparator') return resolveSmartLabel(src, nodes, edges, depth + 1, record);
                
                return getNodeName(src, nodes, edges);
            });
            
            const op = (node.data?.operator || 'AND').toUpperCase();
            return names.length > 0 ? names.join(` ${op} `) : `Logic ${op}`;
        }

        // 4. Handle Math Nodes — the ONE arithmetic auto text (was ArithmeticNode's own copy, 50-21 R10-T10)
        if (node.type === 'math') {
            return resolveMathAutoText(node, nodes, edges, depth);
        }

        // 5. Handle Indicators (Force parameter check)
        if (node.type === 'indicator') {
            return getNodeName(node, nodes, edges);
        }

        // 1. A label that does not look auto-assembled is an authored name: respect it
        //    (only reached by node types without a dedicated auto rule above).
        const currentLabel = node.data?.label || '';
        if (!isAutoAssembledLabel(currentLabel)) {
            return currentLabel;
        }

        // Fallback
        return getNodeName(node, nodes, edges);
    } catch (err) {
        console.error('Error resolving smart label:', err);
        return node.data?.label || node.type || 'Error';
    }
};

//--> arithmetic auto text: "<left> <op> <right>" with compact operand names (folded in from ArithmeticNode)
const MATH_SYMBOLS = { add: '+', sub: '-', mul: '×', div: '÷' };
const resolveMathAutoText = (node, nodes = [], edges = [], depth = 0) => {
    const symbol = MATH_SYMBOLS[String(node?.data?.operator || '').toLowerCase()] || '?';
    const inputs = Array.isArray(node?.data?.inputs) ? node.data.inputs : [];
    const names = inputs.map((input) => {
        const edge = (edges || []).find((e) => e.target === node.id && e.targetHandle === input?.id);
        const source = edge ? (nodes || []).find((n) => n.id === edge.source) : null;
        if (!source) return input?.type === 'constant' ? (input.value ?? 'Val') : 'Val';
        if (source.type === 'data_source') return edge.sourceHandle || 'Mkt';
        if (source.type === 'comparator') return 'Comp';
        if (source.type === 'logic_gate') return (source.data?.operator || 'Logic').toUpperCase();
        if (source.type === 'math') return 'Math';
        return resolveSmartLabel(source, nodes, edges, depth + 1) || source.type;
    });
    return names.length > 0 ? names.join(` ${symbol} `) : 'Math';
};

//--> time_window auto text: the existing condition-detail builder's range ("09:15–15:30")
const resolveTimeWindowAutoText = (node) => {
    const range = buildConditionDetail(node, {})?.text;
    return range ? `Time window ${range}` : 'Time window';
};

/*>>> 50-21 R10 — THE condition/group display-name resolver (one rule, every surface) <<<*/
//* Order: (1) the record's custom_name (comparator / logic_gate / time_window only, trimmed
//* non-empty), (2) a stored data.label the classifier calls custom (legacy carry-over), (3) the
//* CURRENT auto-assembled text. A math node ignores a record custom_name. Node-data
//* custom_name/notes are never a source (Q1/R10-T8: the record is the one authority). Pure,
//* read-only: no write, no network. `recordEntry` is the record's entry for THIS node id.
export const CONDITION_NAME_NODE_TYPES = ['comparator', 'logic_gate', 'time_window'];

export const resolveAutoText = (node, nodes = [], edges = [], depth = 0, record = undefined) => {
    if (!node) return 'Unknown Node';
    if (node.type === 'time_window') return resolveTimeWindowAutoText(node);
    return resolveSmartLabel(node, nodes, edges, depth, record);
};

//--> the authored half of the rule (record custom_name > legacy custom data.label), or null. ONE place:
//    resolveDisplayName and the operand resolvers (A-4) both read it, so no surface re-implements it.
export const resolveAuthoredName = (node, recordEntry = undefined) => {
    if (!node) return null;
    const isCondition = CONDITION_NAME_NODE_TYPES.includes(node.type);
    if (isCondition) {
        const customName = getCustomName(recordEntry);
        if (customName) return customName;
    }
    if (isCondition || node.type === 'math') {
        const label = node.data?.label;
        if (!isAutoAssembledLabel(label)) return label;
    }
    return null;
};

export const resolveDisplayName = (node, nodes = [], edges = [], recordEntry = undefined, record = undefined) => {
    if (!node) return 'Unknown Node';
    const authored = resolveAuthoredName(node, recordEntry);
    if (authored) return authored;
    if (CONDITION_NAME_NODE_TYPES.includes(node.type) || node.type === 'math') {
        return resolveAutoText(node, nodes, edges, 0, record);
    }
    return resolveSmartLabel(node, nodes, edges, 0, record);
};

//--> Tree operand segments identify the graph node first and carry any authored name as context.
//    This deliberately differs from resolveDisplayName, whose job is to put authored condition names first.
//    The properties panel and segments still share the same authored-name authorities.
export const resolveOperandSegmentText = (
    node, field, nodes = [], edges = [], recordEntry = undefined, record = undefined,
) => {
    if (!node) return { primary: 'Unknown Node', authoredName: null };
    let primary;
    if (node.type === 'data_source') {
        primary = resolveOperandSourceName(node, field, nodes, edges);
    } else if (node.type === 'constant') {
        primary = node.data?.value != null && node.data.value !== ''
            ? String(node.data.value)
            : 'Constant';
    } else if (node.type === 'indicator') {
        primary = getNodeName(node, nodes, edges);
    } else if (node.type === 'math') {
        primary = resolveAutoText(node, nodes, edges, 0, record);
    } else {
        primary = resolveAutoText(node, nodes, edges, 0, record);
    }

    const constantLabel = node.type === 'constant' && typeof node.data?.label === 'string'
        ? node.data.label.trim() || null
        : null;
    const authoredName = node.type === 'data_source'
        ? null
        : getCustomName(recordEntry) || resolveAuthoredName(node, recordEntry) || constantLabel;
    return {
        primary,
        authoredName: authoredName && authoredName !== primary ? authoredName : null,
    };
};

//--> 50-21 R10 / R13 — the ONE "name detail" rule every surface shares: while a condition node carries a
//    record custom_name, its CURRENT auto-assembled text rides beside it (subtitle / tooltip / row detail);
//    notes are carried verbatim only when non-empty after trim. Non-condition nodes get neither.
export const resolveNameDetail = (node, nodes = [], edges = [], recordEntry = undefined, record = undefined) => {
    if (!node || !CONDITION_NAME_NODE_TYPES.includes(node.type)) return { autoText: null, notes: null };
    const notes = recordEntry?.notes;
    return {
        autoText: getCustomName(recordEntry) ? resolveAutoText(node, nodes, edges, 0, record) : null,
        notes: typeof notes === 'string' && notes.trim() !== '' ? notes : null,
    };
};

/**
 * Helper to get a short, descriptive name for a node, including parameters.
 */
export const getNodeName = (node, nodes, edges) => {
    if (!node) return 'Val';
    
    // Data Source
    if (node.type === 'data_source') return node.data?.field || 'Mkt';

    // Recursion checks
    if (node.type === 'comparator') return 'Comp'; 
    if (node.type === 'logic_gate') return (node.data?.operator || 'Logic').toUpperCase();

    // Standard Label or Type
    let label = node.data?.label || node.data?.indicator?.toUpperCase() || node.type;

    // Append Params for Indicators
    if (node.type === 'indicator' && node.data?.params) {
        // Expand to include common parameters for various indicators
        const { 
            period, length, window,       // Main Lookback
            fast, slow,                   // MACD / Oscillators
            stdDev, multiplier, mult,     // Bollinger / SuperTrend
            signal, smooth,               // Smoothing
            offset                        // ZLSMA / Displacement
        } = node.data.params;

        const params = [];

        // 1. Primary Lookback
        if (period) params.push(period);
        else if (length) params.push(length);
        else if (window) params.push(window);

        // 2. Dual Params (Fast/Slow)
        if (fast && slow) params.push(`${fast}/${slow}`);

        // 3. Secondary Params
        if (signal) params.push(signal);
        if (smooth) params.push(smooth);
        if (stdDev) params.push(stdDev);
        if (multiplier) params.push(multiplier);
        if (mult) params.push(mult);
        if (offset) params.push(offset);
        
        if (params.length > 0) {
            // Avoid double labelling if the label already contains the params
            if (!label.includes(`(${params[0]}`)) {
                    label = `${label} (${params.join(',')})`;
            }
        }

        // Append timeframe for MTF clarity (e.g. "SMA (20) 15m")
        if (node.data?.timeframe) {
            label = `${label} ${node.data.timeframe}`;
        }
    }
    
    return label;
};

/**
 * nodeDirection — canonical, case-insensitive direction ('long' | 'short' | null).
 * Mirrors backend exit_direction() (graph_normalizer.py:596-611): data.type upper-substring
 * ('LONG'/'LONG RISK' -> 'long'), else action verb (enter_long/exit_long -> 'long'), else null.
 * The SINGLE source every direction classifier calls — a new consumer cannot re-introduce the
 * exact-case bug (ledger rows 2a/2b/2c).
 */
export const nodeDirection = (node) => {
    const t = (node?.data?.type || '').toUpperCase();
    if (t.includes('LONG')) return 'long';
    if (t.includes('SHORT')) return 'short';
    const a = (node?.data?.action || '').toLowerCase();
    if (a === 'enter_long' || a === 'exit_long') return 'long';
    if (a === 'enter_short' || a === 'exit_short') return 'short';
    return null;
};

/**
 * mirrorExecutionQtyToData — #18 money-path. Returns a node list where each node carrying a numeric
 * data.execution.quantity also has data.qty set to the SAME value. data.qty is the field the backend
 * evaluator SIZES from (graph_evaluator.py:1861): Signal.qty is LOTS and the OMS converts x lot_size
 * once at intake, and the OrderCard quantity is that same LOT unit ("N lots x lot_size = units") — so
 * this is a DIRECT copy, never lots x lot_size. Without it the backend falls back to the default 1.0
 * lot and the user's typed quantity is ignored on every FE-authored strategy. Idempotent (a node
 * already at the right data.qty is returned unchanged — no version churn). Nodes without a numeric
 * execution.quantity are untouched. Exits ignore data.qty (they size off the live position,
 * graph_evaluator.py:1862), so carrying it there is harmless.
 */
export const mirrorExecutionQtyToData = (nodes = []) =>
    (nodes || []).map((node) => {
        const q = node?.data?.execution?.quantity;
        if (q == null || q === '') return node;
        const qtyLots = Number(q);
        if (!Number.isFinite(qtyLots) || node?.data?.qty === qtyLots) return node;
        return { ...node, data: { ...node.data, qty: qtyLots } };
    });

/**
 * triggerSide — which Entry/Exit side array a signal/position_trigger belongs to
 * ('longEntry' | 'longExit' | 'shortEntry' | 'shortExit' | null). The ONE trigger classifier;
 * sl/tp go through nodeDirection (case-insensitive). time_exit / legacy buy,sell / unknown -> null.
 */
const triggerSide = (node) => {
    const action = node?.data?.action;
    if (action === 'enter_long') return 'longEntry';
    if (action === 'exit_long') return 'longExit';
    if (action === 'enter_short') return 'shortEntry';
    if (action === 'exit_short') return 'shortExit';
    if (action === 'sl' || action === 'tp') {
        const dir = nodeDirection(node);
        if (dir === 'long') return 'longExit';
        if (dir === 'short') return 'shortExit';
    }
    return null;
};

/*>>> TRIGGER CONDITION TREE (50-17) <<<*/
//* ONE graph walk for "what feeds this trigger". Both the Entry/Exit dashboard rows and the
//  Monitoring rows render the tree this returns; collectTriggerConditions is its flat id view.

//--> Inbound sources of every node: edges de-duplicated by source + target + handle, sources sorted by
//    id so the same graph gives the same order on every run and with edge order shuffled (D12).
const buildInbound = (edges) => {
    const inbound = new Map();
    const seenEdge = new Set();
    (edges || []).forEach((e) => {
        const k = `${e.source}|${e.target}|${e.targetHandle ?? ''}`;
        if (seenEdge.has(k)) return;
        seenEdge.add(k);
        if (!inbound.has(e.target)) inbound.set(e.target, []);
        inbound.get(e.target).push(e);
    });
    return inbound;
};

//--> Mirrors backend _get_input_values: data.inputs first, else inbound edges ordered by
//    normalize_handle_id(targetHandle) then edge id (graph_inputs.py:18-22). A NOT gate reads ONLY this input.
const firstInputOf = (node, inEdges, byId) => {
    const declared = node?.data?.inputs?.[0]?.nodeId;
    //  a declared input with no edge into this gate is not what the gate is wired to: fall back to the edges
    let src = declared && (!inEdges?.length || inEdges.some((e) => e.source === declared)) ? declared : null;
    if (!src && inEdges?.length) {
        const norm = (h) => String(h ?? '').replace('input_', '');
        const ordered = [...inEdges].sort((a, b) => {
            const ha = norm(a.targetHandle), hb = norm(b.targetHandle);
            if (ha !== hb) return ha < hb ? -1 : 1;
            const ia = String(a.id ?? ''), ib = String(b.id ?? '');
            return ia < ib ? -1 : ia > ib ? 1 : 0;
        });
        src = ordered[0].source;
    }
    //  a math node is transparent in the tree, so its own children cannot be matched by id: no pin
    if (!src || byId.get(src)?.type === 'math') return null;
    return src;
};

/**
 * collectTriggerTree — the gate/condition tree feeding ONE trigger: { node, children, firstInputId? }.
 * Walks inbound edges (target -> source). comparator / logic_gate / time_window become tree nodes,
 * math is transparent (its children are spliced into the parent), every other type is neither
 * recorded nor walked, and another trigger (signal / position_trigger) is never crossed, so the
 * walk never enters sig_enter_* into an exit's inputs. EVERY edge is followed: a node wired to the
 * trigger AND into a gate appears at both positions. A per-call memo shares one subtree under every
 * parent (O(V+E)); an in-progress guard drops an edge back to a node still being built, so cycles end.
 */
export const collectTriggerTree = (triggerId, nodes = [], edges = []) => {
    const byId = new Map((nodes || []).map((n) => [n.id, n]));
    const inbound = buildInbound(edges);
    const memo = new Map();
    const mathMemo = new Map();
    const inProgress = new Set();

    const childrenOf = (parentId) => {
        const out = [];
        const seen = new Set();
        const push = (t) => {
            if (!seen.has(t.node.id)) { seen.add(t.node.id); out.push(t); }
        };
        const sources = [...new Set((inbound.get(parentId) || []).map((e) => e.source))].sort();
        sources.forEach((srcId) => {
            const src = byId.get(srcId);
            if (!src || TRIGGER_NODE_TYPES.includes(src.type)) return;
            if (LOGIC_NODE_TYPES.includes(src.type)) {
                const t = build(srcId);
                if (t) push(t);
            } else if (src.type === 'math') {
                if (inProgress.has(srcId)) return;
                if (!mathMemo.has(srcId)) {
                    inProgress.add(srcId);
                    mathMemo.set(srcId, childrenOf(srcId));
                    inProgress.delete(srcId);
                }
                mathMemo.get(srcId).forEach(push);
            }
        });
        return out;
    };

    function build(id) {
        if (memo.has(id)) return memo.get(id);
        if (inProgress.has(id)) return null;
        inProgress.add(id);
        const node = byId.get(id);
        const t = { node, children: childrenOf(id) };
        if (node.type === 'logic_gate' && gateOperator(node) === 'NOT') {
            t.firstInputId = firstInputOf(node, inbound.get(id), byId);
        }
        inProgress.delete(id);
        memo.set(id, t);
        return t;
    }

    inProgress.add(triggerId);
    return { node: byId.get(triggerId) || { id: triggerId }, children: childrenOf(triggerId) };
};

/** flattenTriggerTree — unique node ids in preorder, the root (trigger) excluded. */
export const flattenTriggerTree = (tree) => {
    const out = [];
    const seen = new Set();
    const walk = (t) => {
        (t?.children || []).forEach((c) => {
            if (!seen.has(c.node.id)) { seen.add(c.node.id); out.push(c.node.id); }
            walk(c);
        });
    };
    walk(tree);
    return out;
};

/**
 * collectTriggerConditions — ids of the comparator/logic_gate/time_window nodes that feed ONE trigger
 * (the flat view of collectTriggerTree: same id SET as the former stack walk, preorder-by-id order).
 * Edges are the single source of wiring truth (graph_normalizer.py:797).
 */
export const collectTriggerConditions = (nodes = [], edges = [], triggerId) =>
    flattenTriggerTree(collectTriggerTree(triggerId, nodes, edges));

/**
 * gateOperator — the ONE structural reader of a logic_gate's operator: 'AND' | 'OR' | 'NOT' or null.
 * Case-insensitive, missing operator = AND, exactly as the backend (graph_evaluator.py:2116,2137-2138);
 * an unknown non-empty operator, including '', is null (the backend evaluates it to False).
 */
export const gateOperator = (node) => {
    const op = String(node?.data?.operator ?? 'and').toLowerCase();
    if (op === 'and') return 'AND';
    if (op === 'or') return 'OR';
    if (op === 'not') return 'NOT';
    return null;
};

/**
 * rowGateState — the ONLY value-vs-required-polarity comparison. 'satisfied' | 'unsatisfied' | 'neutral'.
 * ctx = { invalidReason, anyOf, parentSatisfied, parentNeeded }. Unknown (null value / invalid input)
 * is never satisfied or unsatisfied; under an any-of gate whose parent is already satisfied a
 * non-matching child is simply not needed.
 */
export const rowGateState = (value, requiredPolarity, ctx = {}) => {
    const { invalidReason, anyOf, parentSatisfied, parentNeeded } = ctx;
    if (parentNeeded === false) return 'neutral';
    if (value == null || invalidReason) return 'neutral';
    if (value === requiredPolarity) return 'satisfied';
    if (anyOf && parentSatisfied) return 'neutral';
    return 'unsatisfied';
};

//--> Required polarity / sibling mode a gate hands to its inputs (De Morgan): the gate's own `need` is
//    what its ancestors required of it. NOT flips what its input must be.
const applyGate = (acc, op) => {
    if (op === 'AND') return { need: acc.need, mode: acc.need ? 'all' : 'any' };
    if (op === 'OR') return { need: acc.need, mode: acc.need ? 'any' : 'all' };
    if (op === 'NOT') return { need: !acc.need, mode: 'all' };
    return { need: acc.need, mode: 'all' };
};

/**
 * annotateTriggerTree — flat preorder rows (trigger first) for ConditionRailTree. Pure: reads no edges
 * and is the only caller of rowGateState. `rowOf(id)` is the surface's normalised row
 * { value: true|false|null, invalidReason?, ... }; the rest of it rides along on row.view.
 * Row: { key, parentKey, id, node, kind, depth, gateOp, ancestorOps, polarity, mode, reachable,
 *        needed, satisfied, state, hint, descendantCount, view, summary? }.
 * The trigger row also carries { count: {met, total}, blockers: [{id, view, ...}] }.
 */
export const annotateTriggerTree = (tree, rowOf) => {
    const rows = [];
    if (!tree?.node) return rows;
    const triggerId = tree.node.id;
    const trigger = {
        key: triggerId, parentKey: null, id: triggerId, node: tree.node, kind: 'trigger', depth: 0,
        gateOp: null, ancestorOps: [], polarity: true, mode: 'all', reachable: true, needed: true,
        satisfied: false, state: 'neutral', hint: null, descendantCount: 0, view: null,
    };
    rows.push(trigger);

    //--> returns the rows created at this level so the parent can count its summary / descendants
    const walk = (children, parent, parentAcc, parentQualifies, parentFirstInputId, parentIsNot) => {
        const made = [];
        children.forEach((child) => {
            const node = child.node;
            const view = rowOf(node.id) || {};
            const isGate = node.type === 'logic_gate';
            const gateOp = isGate ? gateOperator(node) : null;
            const reachable = parent.needed && parentQualifies &&
                (!parentIsNot || parentFirstInputId == null || node.id === parentFirstInputId);
            const polarity = parentAcc.need;
            const mode = parentAcc.mode;
            const invalidReason = view.invalidReason || null;
            const parentSatisfied = parent.kind === 'trigger' ? false : parent.satisfied;
            const satisfied = rowGateState(view.value, polarity, { invalidReason }) === 'satisfied';
            const state = rowGateState(view.value, polarity, {
                invalidReason, anyOf: mode === 'any', parentSatisfied, parentNeeded: reachable,
            });
            const needed = reachable && !(mode === 'any' && parentSatisfied && !satisfied);
            let hint = null;
            if (!needed && parentQualifies) hint = 'not needed';
            else if (polarity === false && mode === 'all' && parentQualifies) hint = 'needs False';
            const row = {
                key: `${parent.key}/${node.id}`, parentKey: parent.key, id: node.id, node,
                kind: isGate ? 'gate' : 'leaf', depth: parent.depth + 1, gateOp,
                ancestorOps: parent.kind === 'gate' ? [...parent.ancestorOps, parent.gateOp] : parent.ancestorOps,
                polarity, mode, reachable, needed, satisfied, state, hint, descendantCount: 0, view,
                invalidReason,
            };
            rows.push(row);
            made.push(row);
            const idxBefore = rows.length;
            const childAcc = isGate ? applyGate(parentAcc, gateOp) : parentAcc;
            const kids = walk(child.children || [], row, childAcc, isGate && gateOp != null, child.firstInputId, gateOp === 'NOT');
            row.descendantCount = rows.length - idxBefore;
            row.hasNeededDescendant = rows.slice(idxBefore).some((r) => r.needed);
            if (isGate) {
                row.summary = { op: gateOp, total: kids.length, met: kids.filter((k) => k.state === 'satisfied').length };
            }
        });
        return made;
    };

    const top = walk(tree.children || [], trigger, { need: true, mode: 'all' }, true, null, false);
    trigger.descendantCount = rows.length - 1;

    //--> count: over the ROOT GROUP (the sole AND/OR child's inputs, else the trigger's inputs)
    let group = top;
    if (top.length === 1 && top[0].kind === 'gate' && (top[0].gateOp === 'AND' || top[0].gateOp === 'OR')) {
        group = rows.filter((r) => r.parentKey === top[0].key);
    }
    trigger.count = {
        met: group.filter((r) => r.state === 'satisfied').length,
        total: group.filter((r) => r.needed || r.invalidReason).length,
    };

    //--> blockers: needed rows that are unsatisfied or invalid; a gate only when no needed row sits under it
    const seen = new Set();
    trigger.blockers = [];
    rows.slice(1).forEach((r) => {
        if (!r.needed || !(r.state === 'unsatisfied' || r.invalidReason)) return;
        if (r.kind === 'gate' && r.hasNeededDescendant) return;
        if (seen.has(r.id)) return;
        seen.add(r.id);
        trigger.blockers.push(r);
    });
    return rows;
};

/*>>> D25 — CONDITION TEXT WITH LIVE VALUES (the ONE value-substitution function) <<<*/
//  Moved here from SignalEvaluationPanel so both surfaces call one authority.
function resolveInputValue(input, valueOf) {
    if (!input) return undefined;
    if (input.type === 'constant') return input.value;
    if (input.type !== 'link') return input.value;

    return pickInputField(input, valueOf(input.nodeId));
}

//--> the field an input picks out of its source node's value (a multi-output dict, or a scalar)
function pickInputField(input, parentVal) {
    if (input?.field && input.field !== 'value' && parentVal && typeof parentVal === 'object' && input.field in parentVal) {
        return parentVal[input.field];
    }
    //  a whole output dict with no field picked reads as its `value`
    if (parentVal && typeof parentVal === 'object' && !Array.isArray(parentVal) && 'value' in parentVal) return parentVal.value;
    return parentVal;
}

const COMPARATOR_SYMBOLS = { eq: '==', ne: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=' };

//--> a time_window bound: 'HH:MM' literal, or {anchor, offset_minutes} -> open+15m / close-10m / square_off
export const formatWindowBound = (b) => {
    if (b == null || b === '') return 'not set';
    if (typeof b === 'string') return b;
    if (typeof b === 'object' && b.anchor) {
        const off = Math.trunc(Number(b.offset_minutes) || 0);
        return off === 0 ? String(b.anchor) : `${b.anchor}${off > 0 ? '+' : '-'}${Math.abs(off)}m`;
    }
    return 'not set';
};

const finiteOrNull = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * buildConditionDetail(node, live) -> { text, detail } | null.
 *   text   short form shown IN the row (null for comparator / logic_gate: those rows stay compact);
 *   detail full form revealed on a row click.
 * live = { valueOf?(nodeId), valueAt?(nodeId, barsAgo), outcome?, ltp?, stop?, target?, noPosition?, inPosition? }. comparator and logic_gate
 * detail strings are the former Monitoring strings plus, when the row's evaluated value at the SAME bar is known
 * (`live.outcome` true|false), the outcome suffix ' → true' / ' → false' (50-17: an operand line alone read as an assertion). A self-contained node (sl / tp / time_exit trigger,
 * time_window) states its own parameters, with the delivered level / LTP substituted when present.
 */
//--> 50-17: HH:MM of an epoch (seconds or ms) in the exchange timezone (default Asia/Kolkata); null when unusable
const DEFAULT_EXCHANGE_TZ = 'Asia/Kolkata';
const tzLabel = (tz) => ((tz || DEFAULT_EXCHANGE_TZ) === 'Asia/Kolkata' ? 'IST' : (tz || DEFAULT_EXCHANGE_TZ));
const formatBarClock = (barTime, tz) => {
    const n = Number(barTime);
    if (barTime == null || !Number.isFinite(n) || n <= 0) return null;
    const ms = n > 1e11 ? n : n * 1000;
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: tz || DEFAULT_EXCHANGE_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(new Date(ms));
};

export const buildConditionDetail = (node, live = {}) => {
    const data = node?.data || {};
    const valueOf = Object.prototype.hasOwnProperty.call(live || {}, 'valueOf') ? live.valueOf : () => undefined;
    //* 50-17 — valueAt(nodeId, barsAgo): the source node's value N bars before the evaluated bar (hovered
    //  bar, else latest), read from the per-bar series the chart draws. Absent -> offset values unknown.
    const valueAt = typeof live?.valueAt === 'function' ? live.valueAt : null;
    const op = String(data.operator || '').toLowerCase();
    const gateOp = node?.type === 'logic_gate' ? gateOperator(node) : null;
    const inputs = Array.isArray(data.inputs) ? data.inputs : [];
    //* 50-17 — the row's own evaluated value (same bar as the operands); unknown -> no suffix, never a guess
    const outcomeTag = live?.outcome === true ? ' → true' : live?.outcome === false ? ' → false' : '';

    if (node?.type === 'comparator' && inputs.length >= 2) {
        const cmp = COMPARATOR_SYMBOLS[op] || op || '==';
        const sideOf = (input) => {
            const raw = resolveInputValue(input, valueOf);
            const named = input?.field && input?.field !== 'value';
            const current = named ? `${input.field}=${formatNodeValue(raw)}` : formatNodeValue(raw);
            const tag = input?.type === 'link' ? barsAgoTag(input) : '';
            if (!tag) return { text: current, note: null };
            //! 50-17 — an offset input compares the value N bars before the evaluated bar: read THAT bar from
            //  the chart's per-bar series. The current-bar value is never shown as if it were the compared one.
            const name = `${named ? input.field : 'value'} (${tag})`;
            const past = valueAt ? pickInputField(input, valueAt(input.nodeId, Math.trunc(Number(input.offset)))) : undefined;
            if (past !== undefined && past !== null) return { text: `${name} = ${formatNodeValue(past)}`, note: null };
            return { text: name, note: `${tag.replace(/ /g, '-')} value not in the chart series` };
        };
        const l = sideOf(inputs[0]);
        const r = sideOf(inputs[1]);
        const notes = [l.note, r.note].filter(Boolean);
        return { text: null, detail: `${l.text} ${cmp} ${r.text}${outcomeTag}${notes.length ? ` (${notes.join('; ')})` : ''}` };
    }

    if (node?.type === 'logic_gate' && inputs.length > 0) {
        if (gateOp === 'NOT') {
            return { text: null, detail: `NOT ${formatNodeValue(resolveInputValue(inputs[0], valueOf))}${outcomeTag}` };
        }
        if (gateOp === 'AND' || gateOp === 'OR') {
            const vals = inputs.map((i) => formatNodeValue(resolveInputValue(i, valueOf)));
            return { text: null, detail: `${vals.join(` ${gateOp} `)}${outcomeTag}` };
        }
    }

    if (node?.type === 'time_window') {
        const range = `${formatWindowBound(data.from)}–${formatWindowBound(data.to)}`;
        //* 50-17: with the evaluated bar's START time -> 'Bar 21:45 IST vs 09:00–18:00 IST → false' (backend rule:
        //  half-open from <= bar_start < to, exchange tz). No bar time -> today's static text.
        const barHHMM = formatBarClock(live?.barTime, live?.exchangeTz);
        if (barHHMM) {
            const tz = tzLabel(live?.exchangeTz);
            const out = typeof live?.outcome === 'boolean' ? ` → ${live.outcome}` : '';
            return { text: range, detail: `Bar ${barHHMM} ${tz} vs ${range} ${tz}${out}` };
        }
        return { text: range, detail: `Inside ${range} exchange time` };
    }

    if (TRIGGER_NODE_TYPES.includes(node?.type)) {
        const action = data.action;
        const params = data.params || {};
        if (action === 'sl' || action === 'tp') {
            if (params.exit_mode === 'CUSTOM') return null;
            const isSl = action === 'sl';
            const type = String(params[isSl ? 'sl_type' : 'tp_type'] ?? 'PERCENT').toUpperCase();
            const rawVal = params[isSl ? 'sl_value' : 'tp_value'];
            const val = rawVal == null || rawVal === '' ? 1 : rawVal;
            const atr = params.atr_period ?? params.period ?? 14;
            const rule = type === 'ATR' ? `${formatNodeValue(Number(val))} × ATR(${atr})`
                : type === 'FIXED' ? `${formatNodeValue(Number(val))} pts`
                    : type === 'PERCENT' ? `${formatNodeValue(Number(val))}%`
                        : `${type} ${formatNodeValue(Number(val))}`;
            const trailing = isSl && params.exit_mode === 'TRAILING';
            const noun = isSl ? (trailing ? 'trail stop' : 'stop') : 'target';
            const level = finiteOrNull(isSl ? live?.stop : live?.target);
            const ltp = finiteOrNull(live?.ltp);
            let text;
            if (level != null && ltp != null) text = `LTP ${formatNodeValue(ltp)} / ${noun} ${formatNodeValue(level)}`;
            else if (level != null) text = `${noun} ${formatNodeValue(level)} · ${rule}`;
            else if (ltp != null) text = `${rule} · LTP ${formatNodeValue(ltp)}`;
            else text = rule;
            const parts = [`${isSl ? 'Stop loss' : 'Take profit'} ${rule} from entry${isSl ? (trailing ? ', trailing' : ', standard') : ''}`];
            if (level != null) parts.push(`${noun} ${formatNodeValue(level)}`);
            if (ltp != null) parts.push(`LTP ${formatNodeValue(ltp)}`);
            if (level != null) {
                //--> breach side follows exit_resolver.py:44-56: long sl low <=, long tp high >=, short sl high >=, short tp low <=
                const side = nodeDirection(node);
                if (side) {
                    const lowSide = (isSl && side === 'long') || (!isSl && side === 'short');
                    parts.push(`fires when a bar's ${lowSide ? 'low ≤' : 'high ≥'} ${formatNodeValue(level)}`);
                }
            } else {
                //* 50-17: flat / in-position come from the position authority (live.noPosition / live.inPosition),
                //  never from a missing level: flat -> say so; in position with no drawn level -> 'not yet known'.
                const noun2 = isSl ? 'stop' : 'target';
                if (live?.noPosition) parts.push(typeof live.noPosition === 'string' ? live.noPosition : 'no open position');
                else if (live?.inPosition) parts.push(`${noun2} level not yet known`);
                else parts.push(`${noun2} level not delivered to this view`);
            }
            return { text, detail: parts.join(' · ') };
        }
        if (action === 'time_exit') {
            const n = finiteOrNull(params.max_bars);
            if (n != null && n > 0) {
                return {
                    text: `after ${n} ${n === 1 ? 'bar' : 'bars'}`,
                    detail: `Time exit after ${n} ${n === 1 ? 'bar' : 'bars'} in position · bars elapsed not delivered to this view`,
                };
            }
            return { text: 'max bars not set', detail: 'Time exit has no bar limit set and never fires' };
        }
    }

    return null;
};

/**
 * Categorizes nodes into functionally distinct groups for UI organization.
 * Used by R&D Dashboard and Strategy Builder.
 * Side membership (longEntry/longExit/shortEntry/shortExit) of a logic node is a backward walk
 * over edges from each classified trigger, stopping at triggers; a logic node no trigger reaches
 * stays only in `logic`.
 *
 * DESIGN NOTE:
 * We use a centralized config (nodeVisualConfig) for node types to ensure
 * that as new nodes are added (e.g. 'script', 'custom_code'), they are
 * automatically picked up here without manual updates.
 */
export const categorizeNodes = (nodes = [], edges = []) => {
    const categories = {
        dataSource: [],
        chartType: [],
        indicators: [],
        math: [],
        logic: [], // Math + Logic nodes for R&D Dashboard "Logic Ops" section
        custom: [],
        longEntry: [],
        longExit: [],
        shortEntry: [],
        shortExit: []
    };

    if (!nodes || nodes.length === 0) return categories;

    //--> Pre-pass: node id -> Set of side keys of the triggers whose walk reaches it.
    const sidesById = new Map();
    nodes.forEach((t) => {
        if (!TRIGGER_NODE_TYPES.includes(t.type)) return;
        const side = triggerSide(t);
        if (!side) return;
        collectTriggerConditions(nodes, edges, t.id).forEach((id) => {
            if (!sidesById.has(id)) sidesById.set(id, new Set());
            sidesById.get(id).add(side);
        });
    });

    nodes.forEach(node => {
        if (node.type === 'data_source') {
            categories.dataSource.push(node);
        } 
        
        // 1. Chart Visuals (Candles, Lines, Areas)
        else if (CHART_VISUAL_TYPES.includes(node.type)) {
            categories.chartType.push(node);
        } 
        
        // 2. Math Nodes (Arithmetic)
        else if (node.type === 'math') {
            // Math nodes appear in both 'math' and 'logic' categories:
            // - 'math' for Node Properties Panel navigation
            categories.math.push(node);
        } 
        
        // 3. Indicators (RSI, SMA, etc.)
        else if (node.type === 'indicator') {
            categories.indicators.push(node);
        } 
        
        // 4. Custom Scripts
        else if (node.type === 'script' || node.type === 'custom_code') {
            categories.custom.push(node);
        } 
        
        // 5. Triggers (Signals & Position triggers)
        else if (TRIGGER_NODE_TYPES.includes(node.type)) {
             const side = triggerSide(node);
             if (side) categories[side].push(node);
        } 
        
        // 6. Logic Gates & Comparators
        else if (LOGIC_NODE_TYPES.includes(node.type)) {
             // 1. Track in logic category for generic visibility
             categories.logic.push(node);

             // 2. Side membership comes only from the backward walk (pre-pass); no default side.
             const sides = sidesById.get(node.id);
             if (sides) {
                 ['longEntry', 'longExit', 'shortEntry', 'shortExit'].forEach((k) => {
                     if (sides.has(k)) categories[k].push(node);
                 });
             }
        }
    });

    // Post-Processing: Sort indicators to prioritize visible ones.
    // Why? Users want to see the active/visible indicators at the top of the list
    // in the dashboard, rather than buried under hidden/helper indicators.
    categories.indicators.sort((a, b) => {
        const aHidden = a.data?.is_visible === false ? 1 : 0;
        const bHidden = b.data?.is_visible === false ? 1 : 0;
        return aHidden - bHidden;
    });

    return categories;
};

//--> Strategy-logic / indicator taxonomy (moved from IndicatorsPanelTab.jsx — one authority for the
//*   Toolbox, the Indicators panel and the responsive dashboard; board 50-15).
export const MATH_LOGIC_TYPES = ['math', ...LOGIC_NODE_TYPES];

//! Every node type that belongs under "Strategy Logic" (math + logic/comparator/time_window +
//! signal/position_trigger) — the one eligible list the panel and the dashboard both group.
export const STRATEGY_LOGIC_NODE_TYPES = ['math', ...LOGIC_NODE_TYPES, ...TRIGGER_NODE_TYPES];


//! D56 — Strategy Logic subgroup resolution. Math/comparator/logic_gate always go to "Math &
//! Logic" (mirrors the Toolbox). For signal/position_trigger/time_window: enter_long/exit_long →
//! Long Position, enter_short/exit_short → Short Position; sl/tp/time_exit have no inherent side
//! in `action` so fall to the SAME `nodeDirection` authority `categorizeNodes` itself uses
//! (nodeUtils.js:183 — data.type upper-substring, case-insensitive). A node `nodeDirection` can't
//! resolve (no data.type, no direction-bearing action — e.g. a bare time_window) is NEVER
//! guessed into Long/Short — it lands in a dedicated "Position" bucket instead.
export const CALCULATED_VALUES_CATEGORY = 'Calculated Values';

export const LOGIC_SUBGROUP = Object.freeze({
  LONG: 'Long Position', SHORT: 'Short Position', MATH_LOGIC: 'Math & Logic', POSITION: 'Position',
});
export const AUTHORING_GROUP = Object.freeze({
  INDICATORS: 'Indicators',
  STRATEGY_LOGIC: 'Strategy Logic',
  VISUALIZATION: 'Visualization',
  OTHER: 'Other',
});
export const CUSTOM_MATH_SUBGROUP = 'Custom & Math';

const positionSubgroup = (node) => {
  const action = node.data?.action;
  if (action === 'enter_long' || action === 'exit_long') return LOGIC_SUBGROUP.LONG;
  if (action === 'enter_short' || action === 'exit_short') return LOGIC_SUBGROUP.SHORT;
  const dir = nodeDirection(node);
  if (dir === 'long') return LOGIC_SUBGROUP.LONG;
  if (dir === 'short') return LOGIC_SUBGROUP.SHORT;
  return LOGIC_SUBGROUP.POSITION;
};
//! Fixed render order — mirrors the Toolbox's Long → Short → Math & Logic layout; the
//! never-guessed "Position" fallback renders last, after every named Toolbox section.
export const STRATEGY_LOGIC_SUBGROUP_ORDER = [LOGIC_SUBGROUP.LONG, LOGIC_SUBGROUP.SHORT, LOGIC_SUBGROUP.MATH_LOGIC, LOGIC_SUBGROUP.POSITION];

const displayCategory = (category) => {
  const value = String(category || '').trim();
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : 'Custom';
};

const registryCategory = (item, categoryByIndicator) => {
  const indicator = item?.data?.indicator || item?.indicator || item?.id;
  const mapped = indicator ? categoryByIndicator?.[indicator] : undefined;
  if (typeof mapped === 'string') return mapped;
  if (mapped && typeof mapped === 'object') return mapped.category;
  return item?.category;
};

export const resolveAuthoringGroup = (item, categoryByIndicator = {}) => {
  const type = item?.type;
  if (type === 'indicator' || type === 'script' || type === 'custom_code') {
    if (item?.data?.isFieldExtractor) {
      return { group: AUTHORING_GROUP.INDICATORS, subgroup: CALCULATED_VALUES_CATEGORY };
    }
    const category = registryCategory(item, categoryByIndicator);
    if (String(category || '').trim().toLowerCase() === 'custom') {
      return { group: AUTHORING_GROUP.INDICATORS, subgroup: CUSTOM_MATH_SUBGROUP };
    }
    return { group: AUTHORING_GROUP.INDICATORS, subgroup: displayCategory(category) };
  }
  if (type === 'math') {
    return { group: AUTHORING_GROUP.INDICATORS, subgroup: CUSTOM_MATH_SUBGROUP };
  }
  if (type === 'time_window') {
    return { group: AUTHORING_GROUP.STRATEGY_LOGIC, subgroup: positionSubgroup(item) };
  }
  if (LOGIC_NODE_TYPES.includes(type)) {
    return { group: AUTHORING_GROUP.STRATEGY_LOGIC, subgroup: LOGIC_SUBGROUP.MATH_LOGIC };
  }
  if (TRIGGER_NODE_TYPES.includes(type)) {
    return { group: AUTHORING_GROUP.STRATEGY_LOGIC, subgroup: positionSubgroup(item) };
  }
  if (type === 'data_source' || type === 'chart_output' || type === 'chart_node' || CHART_VISUAL_TYPES.includes(type)) {
    return { group: AUTHORING_GROUP.VISUALIZATION, subgroup: 'Chart' };
  }
  return { group: AUTHORING_GROUP.OTHER, subgroup: displayCategory(item?.category || type || 'Other') };
};

const authoringGroupId = (group, subgroup) => `${group}::${subgroup}`
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const authoringGroupOrder = (group) => [
  AUTHORING_GROUP.INDICATORS,
  AUTHORING_GROUP.STRATEGY_LOGIC,
  AUTHORING_GROUP.VISUALIZATION,
  AUTHORING_GROUP.OTHER,
].indexOf(group);

const authoringSubgroupOrder = (group, subgroup) => {
  if (group === AUTHORING_GROUP.STRATEGY_LOGIC) {
    const index = STRATEGY_LOGIC_SUBGROUP_ORDER.indexOf(subgroup);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  }
  return subgroup;
};

export const groupAuthoringItems = (items, categoryByIndicator = {}) => {
  const buckets = new Map();
  (items || []).forEach((item) => {
    const { group, subgroup } = resolveAuthoringGroup(item, categoryByIndicator);
    const id = authoringGroupId(group, subgroup);
    if (!buckets.has(id)) {
      buckets.set(id, { id, key: subgroup, group, subgroup, title: subgroup, nodes: [] });
    }
    buckets.get(id).nodes.push(item);
  });
  return [...buckets.values()].sort((left, right) => {
    const groupDelta = authoringGroupOrder(left.group) - authoringGroupOrder(right.group);
    if (groupDelta) return groupDelta;
    const leftOrder = authoringSubgroupOrder(left.group, left.subgroup);
    const rightOrder = authoringSubgroupOrder(right.group, right.subgroup);
    if (typeof leftOrder === 'number' && typeof rightOrder === 'number') return leftOrder - rightOrder;
    return String(leftOrder).localeCompare(String(rightOrder));
  });
};

// Compatibility delegates retained for existing indicator-only and logic-only callers.
export const resolveIndicatorSubgroup = (node, categoryByIndicator) => (
  resolveAuthoringGroup(node, categoryByIndicator).subgroup
);
export const resolveLogicSubgroup = (node) => resolveAuthoringGroup(node).subgroup;
export const groupIndicatorsBySubgroup = (nodes, categoryByIndicator) => (
  groupAuthoringItems(nodes, categoryByIndicator).map((group) => ({
    key: group.subgroup, title: group.subgroup, nodes: group.nodes,
  }))
);
export const groupLogicBySubgroup = (nodes) => (
  groupAuthoringItems(nodes).map((group) => ({
    key: group.subgroup, title: group.subgroup, nodes: group.nodes,
  }))
);

//--> A-4 / AC10.3 / AC11.4 — an operand's display name is the SAME `resolveDisplayName` answer every
//    other surface shows (record custom_name first). `record` = node id -> record entry (optional).
const operandName = (sourceNode, nodes, edges, record) =>
    resolveDisplayName(sourceNode, nodes, edges, record?.[sourceNode.id], record);

/**
 * Resolves all boolean-outputting nodes connected to a node's inputs
 */
export const resolveBooleanInputs = (node, nodes, edges, data, executionData, latestData, record = undefined) => {
    if (!node.data?.inputs) return {};
    
    const resolved = {};
    node.data.inputs.forEach((input, idx) => {
        const handleId = input.id || `input_${idx}`;
        const edge = edges?.find(e => 
            e.target === node.id && 
            (e.targetHandle === handleId || e.targetHandle === `input_${idx}`)
        );

        if (edge) {
            const sourceNode = nodes?.find(n => n.id === edge.source);
            if (sourceNode) {
                const isBooleanNode = LOGIC_NODE_TYPES.includes(sourceNode.type);
                
                if (isBooleanNode) {
                    const label = operandName(sourceNode, nodes, edges, record);
                    const val = data?.[sourceNode.id] ?? executionData?.[sourceNode.id] ?? latestData?.[sourceNode.id];
                    // null = WAIT (warmup/no data), true/false = actual boolean result
                    resolved[label] = (val !== undefined && val !== null) ? !!val : null;
                }
            }
        }
    });
    return resolved;
};

/**
 * Resolves math node input values and labels
 */
export const resolveMathInputs = (node, nodes, edges, data, executionData, latestData, record = undefined) => {
    if (!node.data?.inputs) return {};
    
    const resolved = {};
    node.data.inputs.forEach((input, idx) => {
        let label = `Input ${idx + 1}`;
        let value = undefined;
        
        if (input.type === 'constant') {
            label = `Const (${input.value || 0})`;
            value = parseFloat(input.value) || 0;
        } else {
            const handleId = input.id || `input_${idx}`;
            const edge = edges?.find(e => 
                e.target === node.id && 
                (e.targetHandle === handleId || e.targetHandle === `input_${idx}`)
            );
            
            if (edge) {
                const sourceNode = nodes?.find(n => n.id === edge.source);
                if (sourceNode) {
                    label = operandName(sourceNode, nodes, edges, record);
                    const sId = sourceNode.id;

                    const resolveValue = (source, id) => {
                        const raw = source?.[id];
                        if (sourceNode.type === 'data_source') {
                            const field = input.field || edge.sourceHandle || 'close';
                            return (typeof raw === 'object' && raw !== null) ? (raw[field] ?? raw['close']) : raw;
                        } else {
                            if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
                                const field = input.field || edge.sourceHandle || 'value';
                                return raw[field] ?? raw['value'];
                            }
                            return raw;
                        }
                    };

                    value = resolveValue(data, sId)
                        ?? resolveValue(executionData, sId)
                        ?? resolveValue(latestData, sId);

                    const field = input.field || edge.sourceHandle;
                    if (field && field !== 'value' && field !== 'close' && !label.includes(field)) {
                        label = `${label} (${field})`;
                    }
                }
            }
        }
        const baseKey = label.length > 20 ? label.substring(0, 17) + '...' : label;
        let finalKey = baseKey;
        let counter = 1;
        while (resolved[finalKey] !== undefined) {
            finalKey = `${baseKey} (${++counter})`;
        }
        resolved[finalKey] = value;
    });
    return resolved;
};

/**
 * Resolves logic node input values (Comparators)
 */
export const resolveLogicInputs = (node, nodes, edges, data, executionData, latestData, record = undefined) => {
    if (!node.data?.inputs) return {};
    
    const resolved = {};
    const inputs = node.data.inputs;

    inputs.forEach((input, idx) => {
        let label = `Input ${idx === 0 ? 'A' : 'B'}`;
        if (inputs.length > 2) label = `Input ${idx + 1}`;
        
        let value = undefined;
        
        if (input.type === 'constant') {
            label = `Const (${input.value || 0})`;
            value = parseFloat(input.value) || 0;
        } else {
            const handleId = input.id || `input_${idx}`;
            const edge = edges?.find(e => 
                e.target === node.id && 
                (e.targetHandle === handleId || e.targetHandle === `input_${idx}`)
            );

            if (edge) {
                const sourceNode = nodes?.find(n => n.id === edge.source);
                if (sourceNode) {
                    label = operandName(sourceNode, nodes, edges, record);
                    const sId = sourceNode.id;
                    
                    if (sourceNode.type === 'data_source') {
                        const field = input.field || edge.sourceHandle || 'close';
                        label = `${label} (${field})`;
                        
                        const resolveFieldValue = (source, id, field) => {
                            const obj = source?.[id];
                            return obj?.[field] ?? obj;
                        };
                        
                        value = resolveFieldValue(data, sId, field) 
                            ?? resolveFieldValue(executionData, sId, field) 
                            ?? resolveFieldValue(latestData, sId, field);
                    } else {
                         const rawVal = data?.[sId] ?? executionData?.[sId] ?? latestData?.[sId];

                         if (typeof rawVal === 'object' && rawVal !== null && !Array.isArray(rawVal)) {
                             const field = edge.sourceHandle || 'value';
                             const extracted = rawVal[field] ?? rawVal['value'];
                             value = extracted;
                             
                             if (edge.sourceHandle && !label.includes(edge.sourceHandle)) {
                                 label = `${label} (${edge.sourceHandle})`;
                             }
                         } else {
                             value = rawVal;
                         }

                         // Convert numeric booleans (1/0 from chart) back to
                         // proper booleans for boolean-type source nodes so
                         // display shows TRUE/FALSE instead of 1.00/0.00
                         if (LOGIC_NODE_TYPES.includes(sourceNode.type) && typeof value === 'number') {
                             value = !!value;
                         }
                    }

                    if (input.offset > 0) {
                        label += ` [${input.offset}]`;
                    }
                }
            }
        }
        const baseKey = label.length > 20 ? label.substring(0, 17) + '...' : label;
        let finalKey = baseKey;
        let counter = 1;
        while (resolved[finalKey] !== undefined) {
            finalKey = `${baseKey} (${++counter})`;
        }
        resolved[finalKey] = value;
    });
    
    return resolved;
};


/**
 * Sanitizes nodes for broadcast/storage by removing non-serializable properties (functions).
 * Use this before sending nodes via BroadcastChannel or saving to DB.
 */
export const sanitizeNodesForSync = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    
    // Deeply sanitize an object to remove non-serializable primitives
    const sanitizeValue = (val, seen = new WeakSet()) => {
        if (val === null || val === undefined) return val;
        
        // Skip functions and React components/icons
        if (typeof val === 'function') return undefined;
        
        // Handle Arrays
        if (Array.isArray(val)) {
            // Check for circular reference in array itself (rare but possible if array added to itself)
            if (seen.has(val)) return undefined; 
            seen.add(val);
            return val.map(item => sanitizeValue(item, seen)).filter(v => v !== undefined);
        }
        
        // Handle Objects
        if (typeof val === 'object' && val !== null) {
            // Check for circular reference
            if (seen.has(val)) return undefined;
            seen.add(val);

            // Check for React component or other non-serializable objects (not exhaustive but covers common cases)
            if (val.$$typeof || val._owner || val.hasOwnProperty('$$typeof')) return undefined;
            
            const clean = {};
            Object.entries(val).forEach(([k, v]) => {
                const sanitized = sanitizeValue(v, seen);
                if (sanitized !== undefined) {
                    clean[k] = sanitized;
                }
            });
            return clean;
        }
        
        return val;
    };


    return nodes.map(node => {
        // Create a shallow copy of the node
        const cleanNode = { ...node };
        
        // Deeply sanitize 'data' object if it exists
        if (cleanNode.data) {
            cleanNode.data = sanitizeValue(cleanNode.data);
        }
        
        return cleanNode;
    });
};

/**
 * Finds the associated entry node for a given SL/TP node based on stage and direction.
 * Preserved for backward-compat — prefers matching by stage when a specific stage is needed.
 *
 * @param {Array} nodes - All nodes in the flow.
 * @param {string} skipNodeId - The ID of the current node to skip (optional).
 * @param {number} targetStage - The stage number to match.
 * @param {boolean} isRiskLong - Whether the SL/TP node is for a LONG position.
 * @param {boolean} isRiskShort - Whether the SL/TP node is for a SHORT position.
 * @returns {Object|null} The associated entry node if found.
 */
export const findAssociatedEntryNode = (nodes, skipNodeId, targetStage, isRiskLong, isRiskShort) => {
    return nodes.find(n => {
        if (skipNodeId && n.id === skipNodeId) return false;
        if (n.type !== 'signal' && n.type !== 'position_trigger') return false;
        if ((n.data?.stage || 1) !== targetStage) return false;

        const nAction = n.data?.action || '';
        const nType = (n.data?.type || '').toUpperCase();
        const isValidEntry = ['enter_long', 'enter_short', 'buy', 'sell'].includes(nAction);
        if (!isValidEntry) return false;

        // Action is authoritative; data.type is a legacy fallback.
        const isEntryLong  = nAction === 'enter_long' || nAction === 'buy'
                             || (!['enter_short', 'sell'].includes(nAction) && nType === 'LONG');
        const isEntryShort = nAction === 'enter_short' || nAction === 'sell'
                             || (!['enter_long',  'buy' ].includes(nAction) && nType === 'SHORT');

        if (isRiskLong && isEntryLong) return true;
        if (isRiskShort && isEntryShort) return true;
        if (!isRiskLong && !isRiskShort) return true;
        return false;
    });
};

/**
 * Finds ALL entry nodes that should connect to a given SL/TP node.
 *
 * Unlike findAssociatedEntryNode, this does NOT filter by stage number because a
 * single exit (SL/TP) legitimately receives edges from every entry stage — e.g. a
 * 3-stage scaled-in long position shares one Stop Loss.
 *
 * Sorting by stage ensures deterministic edge ordering so handle ids are stable
 * AT CREATION TIME. If entry nodes are added or removed after the SL/TP is placed,
 * the C-1 reconciliation path in useNodeFactory (add) and the handleEdgesChange
 * cleanup in StrategyLogicStep (remove) are responsible for keeping data.inputs
 * and edges in sync.
 *
 * @param {Array}   nodes      - All nodes in the flow.
 * @param {string}  skipNodeId - The ID of the current SL/TP node to skip (optional).
 * @param {boolean} isRiskLong  - True when the SL/TP is for a LONG position.
 * @param {boolean} isRiskShort - True when the SL/TP is for a SHORT position.
 * @returns {Object[]} All matching entry nodes sorted by stage (ascending).
 */
export const findAllAssociatedEntryNodes = (nodes, skipNodeId, isRiskLong, isRiskShort) => {
    const matches = nodes.filter(n => {
        if (skipNodeId && n.id === skipNodeId) return false;
        if (n.type !== 'signal' && n.type !== 'position_trigger') return false;

        const nAction = n.data?.action || '';
        const nType   = (n.data?.type || '').toUpperCase();
        const isValidEntry = ['enter_long', 'enter_short', 'buy', 'sell'].includes(nAction);
        if (!isValidEntry) return false;

        // Action is the authoritative direction indicator; data.type is a fallback only
        // for nodes that pre-date explicit action fields (prevents cross-classification
        // when data.type is stale or inconsistent with action).
        const isEntryLong  = nAction === 'enter_long' || nAction === 'buy'
                             || (!['enter_short', 'sell'].includes(nAction) && nType === 'LONG');
        const isEntryShort = nAction === 'enter_short' || nAction === 'sell'
                             || (!['enter_long',  'buy' ].includes(nAction) && nType === 'SHORT');

        // Direction-only match — we do NOT check stage here.
        if (isRiskLong  && isEntryLong)  return true;
        if (isRiskShort && isEntryShort) return true;
        // If direction is unspecified, accept any entry direction.
        if (!isRiskLong && !isRiskShort) return true;
        return false;
    });

    // Sort ascending by stage so input_0 always corresponds to Stage 1.
    matches.sort((a, b) => (a.data?.stage || 1) - (b.data?.stage || 1));
    return matches;
};



/*>>> MODEL INDICATOR TILE (142-37) <<<*/
//--> Output keys that identify the model, not a plotted/readable output.
const MODEL_META_KEYS = ['model_version_id', 'output_schema_version'];
const OPERATOR_SYMBOLS = { lt: '<', lte: '<=', gt: '>', gte: '>=', eq: '==', neq: '!=' };

/**
 * 142-37 loop 2 — per-node FULL outputs from the live WS node results. A reduced multi-output
 * indicator ships its configured scalar as `value` and the whole dict as `metadata.outputs`.
 */
export const deriveLiveNodeOutputs = (liveNodeResults) => {
    const out = {};
    if (!liveNodeResults) return out;
    const isPlainObject = (o) => o && typeof o === 'object' && !Array.isArray(o);
    for (const [nodeId, result] of Object.entries(liveNodeResults)) {
        const o = result?.metadata?.outputs;
        if (isPlainObject(o)) out[nodeId] = o;
        else if (isPlainObject(result?.value)) out[nodeId] = result.value;
    }
    return out;
};

/**
 * 50-17 — { [nodeId]: invalid_reason } from the live WS node results, truthy reasons only. A comparator or
 * gate the evaluator withheld is value False + invalid_reason; the dashboard's flattened values drop it,
 * so the tree reads this map to show such a row as unknown rather than unsatisfied.
 */
export const deriveLiveNodeInvalidReasons = (liveNodeResults) => {
    const out = {};
    if (!liveNodeResults) return out;
    for (const [nodeId, result] of Object.entries(liveNodeResults)) {
        if (result?.invalid_reason) out[nodeId] = result.invalid_reason;
    }
    return out;
};

/** keepIfSameReasons(prev, next) -> prev when both maps hold identical entries, else next (stable identity per tick). */
export const keepIfSameReasons = (prev, next) => {
    if (!prev) return next;
    const pk = Object.keys(prev);
    if (pk.length !== Object.keys(next).length) return next;
    return pk.every((k) => prev[k] === next[k]) ? prev : next;
};

/** True when a live value is a model-style dict (any p_* probability output or argmax). */
export const isModelOutputDict = (v) =>
    typeof v === 'object' && v !== null && !Array.isArray(v) &&
    Object.keys(v).some(k => k.startsWith('p_') || k === 'argmax');

/**
 * Downstream comparator that consumes one output of `nodeId`:
 * { field, text } where text is e.g. "< 0.60" (operator + the constant on the other input).
 * Handles a node-level link input ({type:'link', nodeId, field}) and an edge with a sourceHandle.
 */
export const findComparatorConsumer = (nodeId, nodes = [], edges = []) => {
    for (const cmp of nodes || []) {
        if (cmp?.type !== 'comparator') continue;
        const inputs = cmp.data?.inputs || [];
        for (let idx = 0; idx < inputs.length; idx++) {
            const input = inputs[idx];
            if (!input || input.type === 'constant') continue;
            const edge = (edges || []).find(e => e.target === cmp.id && e.source === nodeId &&
                (e.targetHandle === (input.id || `input_${idx}`) || e.targetHandle === `input_${idx}`));
            const fromNode = input.nodeId === nodeId;
            if (!fromNode && !edge) continue;
            const field = input.field || edge?.sourceHandle;
            if (!field) continue;
            const other = inputs.find((o, j) => j !== idx && o?.type === 'constant');
            const thr = other ? parseFloat(other.value) : NaN;
            const op = cmp.data?.operator;
            const sym = OPERATOR_SYMBOLS[op] || op || '';
            return { field, text: Number.isFinite(thr) ? `${sym} ${thr.toFixed(2)}`.trim() : sym };
        }
    }
    return null;
};

/**
 * Everything the composite tile needs for a model indicator, generic over declared outputs:
 * { values, primaryValue, badge, highlightKey, annotations }.
 */
export const buildModelTile = (node, live, nodes, edges, labels = {}) => {
    const declared = Array.isArray(node?.data?.outputs) ? node.data.outputs : null;
    const keys = (declared?.length ? declared : Object.keys(live))
        .filter(k => !k.startsWith('_') && !MODEL_META_KEYS.includes(k) && live[k] !== undefined);
    const values = {};
    keys.forEach(k => { values[labels?.[k] || k] = live[k]; }); //* 142-37: human label when declared, else the key

    const pKeys = keys.filter(k => k.startsWith('p_') && typeof live[k] === 'number');
    let sideKey = null;
    if (typeof live.argmax === 'number' && pKeys[live.argmax]) sideKey = pKeys[live.argmax];
    else if (pKeys.length) sideKey = pKeys.reduce((a, b) => (live[b] > live[a] ? b : a));
    const primaryValue = sideKey ? `${sideKey.slice(2).toUpperCase()} ${live[sideKey].toFixed(2)}` : undefined;

    const consumer = findComparatorConsumer(node.id, nodes, edges);
    const annotations = {};
    const cKey = consumer ? (labels?.[consumer.field] || consumer.field) : undefined;
    if (consumer && typeof values[cKey] === 'number') {
        annotations[cKey] = `${values[cKey].toFixed(2)} ${consumer.text}`.trim();
    }
    return {
        values,
        primaryValue,
        badge: live.model_version_id ? String(live.model_version_id).slice(0, 8) : null,
        highlightKey: cKey,
        annotations,
    };
};
